import React, { useEffect, useContext } from "react";
import { Box, Button, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import { useTranslation } from "react-i18next";
import { DropdownSelect } from "../../../components/DropdownSelect";
import coursePic from "../../../assets/images/course_pic.png";
import {
  CourseCard,
  CourseCardSummary,
  CourseCardExpansionPanel
} from "../../../components/CourseCard";
import RemoveButton from "../../../components/Buttons/RemoveButton";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import { libraryAllCoursesHome } from "../../../routes/LearningCenterRoutes";
import { ReserveTimeButton } from "../../../components/Buttons";
import { ChangeButton } from "../../../components/Buttons";
import { isIlt } from "../../../utils/course_utils";
import UserAssignmentContext from "../../../context/UserAssignmentContext";
import { courseTypes } from "../../../constants";
import { useAutoClickedAssignment } from "../../../hooks/autoClickedAssignment";
import CourseActionButton from "../../../components/Buttons/CourseActionButton";
import { visionURL } from "../../../config/environment";
import JsonApiService from "../../../services/jsonapiService";
import ExpandedAssignmentsContext from "../../../context/ExpandedAssignmentsContext";

const courseCtoaService = async () =>
  new JsonApiService("course_content", {
    baseUrl: `${visionURL}/learning_center/api`,
    credentials: "include"
  });

const MyCoursesEmptyStateView = () => {
  function handleExploreCoursesClick() {
    dispatch(push(libraryAllCoursesHome));
  }
  const dispatch = useDispatch();
  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Box my={5}>
        <Typography variant="h5" align="center" color="textSecondary">
          You have 0 courses in your library.
        </Typography>
      </Box>
      <Button
        variant="contained"
        color="primary"
        onClick={handleExploreCoursesClick}
      >
        Explore Courses
      </Button>
    </Box>
  );
};

const ChangeEnrollmentButton = ({ assignment, onChangeEnrollmentClick }) => {
  const { t } = useTranslation();
  return assignment &&
    assignment.versions &&
    assignment.enrolledCtoaId &&
    isIlt(assignment) ? (
    <ChangeButton
      key="Change Enrollment"
      text={t("learningPathListView.changeEnrollmentBtn")}
      onClick={() => onChangeEnrollmentClick(assignment)}
    />
  ) : null;
};

export default function MyCourses(props) {
  const {
    courses,
    sortOptions,
    sortOrder,
    loading,
    refetchAssignments
  } = props;
  const { t } = useTranslation();

  const {
    onStartAssignment,
    onDeleteUserAssignment,
    onChangeEnrollment,
    onResumeAssignment,
    onAddToCalendarClick,
    calendarModalOpen,
    setCalendarModalOpen,
    isStarted,
    needsRefetch,
    setNeedsRefetch
  } = useContext(UserAssignmentContext);

  const {
    isAssignmentExpanded,
    alwaysShouldBeExpanded,
    onExpandCourseCard,
    getExpansionPanelItems
  } = useContext(ExpandedAssignmentsContext);

  useAutoClickedAssignment();

  useEffect(() => {
    if (needsRefetch) {
      if (refetchAssignments) {
        refetchAssignments();
        setNeedsRefetch(false);
      }
    }
  }, [needsRefetch]);

  useEffect(() => {
    const fetchSections = async () => {
      if (!calendarModalOpen) return;
      if (
        calendarModalOpen.assignment &&
        !calendarModalOpen.assignment.enrolledCtoaId
      ) {
        onAddToCalendarClick(calendarModalOpen.assignment);
      }
      if (
        calendarModalOpen.assignment &&
        calendarModalOpen.assignment.enrolledCtoaId
      ) {
        const service = await courseCtoaService();
        const response = await service
          .include(["course_sections"])
          .find(calendarModalOpen.assignment.assignedTrainingObjectId)
          .then(response => {
            if (response.status == 200) return response.json();
            else return false;
          })
          .then(response => {
            if (response) {
              let activities = response.included;
              activities = activities.filter(
                activity => activity.attributes.sectionType == "mandatory"
              );
              let firstActivity = activities[0];
              firstActivity = {
                id: firstActivity.id,
                ...firstActivity.attributes,
                courseContentables:
                  firstActivity.relationships.courseContentables.data
              };
              if (firstActivity) {
                onAddToCalendarClick(
                  calendarModalOpen.assignment,
                  firstActivity
                );
              } else {
                onAddToCalendarClick(calendarModalOpen.assignment);
              }
            } else onAddToCalendarClick(calendarModalOpen.assignment);
          });
        return response;
      }
    };
    fetchSections();
  }, [calendarModalOpen.assignment]);

  function handleSortByOptionChange(newSortByOption) {
    if (props.handleSort && typeof props.handleSort === "function")
      props.handleSort(newSortByOption);
  }

  const handleReserveTime = (assignment, open) => {
    setCalendarModalOpen({
      assignment: assignment,
      open: open
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }
  return courses && courses.length > 0 ? (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-end"
      data-qa="my-courses-list"
    >
      <Box mb={{ xs: 0, sm: 2 }} mt={{ xs: 1, lg: 0 }}>
        <DropdownSelect
          options={sortOptions}
          value={sortOrder}
          onChange={handleSortByOptionChange}
          label={t("sortBy")}
        />
      </Box>

      {courses.map((course, index) => {
        const isEnrolledIlt =
          course.courseType === courseTypes.ilt &&
          course.enrolledTrainingObjectId;
        const iltVersion = isEnrolledIlt
          ? course?.versions?.length > 0
            ? course?.versions?.find(
                version => version.id == course.enrolledTrainingObjectId
              )
            : course.enrolledTrainingObject
          : null;
        let cardButtons = [];
        cardButtons.push(
          <CourseActionButton
            courseType={course.courseType}
            isStarted={isStarted(course)}
            isEnrolled={!!course.enrolledCtoaId}
            prereqs={course.prerequisites || []}
            onEnrollClick={() => onStartAssignment(course)}
            onResumeCourseClick={() => onResumeAssignment(course)}
            startDate={course.enrolledTrainingObject?.zonedStartDate}
            endDate={course.enrolledTrainingObject?.zonedEndDate}
            iltClassType={course.enrolledTrainingObject?.typeName}
          />
        );

        const activeVersions = course.versions;
        if (!!activeVersions && activeVersions.length > 0) {
          cardButtons.push(
            <ReserveTimeButton
              key="Reserve Time Button"
              onClick={() => handleReserveTime(course, true)}
              disabled={
                Array.isArray(course.prerequisites) &&
                course.prerequisites.length > 0
              }
            />
          );
        }
        cardButtons.push(
          <ChangeEnrollmentButton
            assignment={course}
            onChangeEnrollmentClick={onChangeEnrollment}
          />
        );

        cardButtons.push(
          <RemoveButton
            key="Remove Button"
            onClick={() => onDeleteUserAssignment(course)}
          />
        );

        return (
          <CourseCard
            key={index}
            scheduledDate={course.scheduledDate}
            scheduledTime={course.scheduledTime}
            endDate={iltVersion ? iltVersion.endDate : null}
            endTime={iltVersion ? iltVersion.endTime : null}
            flags={course.flags}
            title={course.title}
            postScore={course.postScore}
            passingPostScore={course.passingPostScore}
            status={course.status}
            failedStatus={course.failedStatus}
            activitiesSummary={
              course.totalActivities > 0 &&
              course.enrolledCtoaId && (
                <CourseCardSummary
                  expanded={isAssignmentExpanded(course)}
                  onActivitiesExpandedChange={() =>
                    onExpandCourseCard(!isAssignmentExpanded(course), course)
                  }
                  totalActivities={course.totalActivities}
                  numActivitiesCompleted={course.numActivitiesCompleted}
                  showProgress={course.courseType !== courseTypes.policy}
                />
              )
            }
            buttons={cardButtons}
            expansionPanel={
              <CourseCardExpansionPanel
                items={getExpansionPanelItems(course)}
                expanded={
                  isAssignmentExpanded(course) || alwaysShouldBeExpanded(course)
                }
                passingPostScore={course.passingPostScore}
                description={course.content}
                assignmentRequirements={course.requirements}
                iltInfo={course.iltInfo}
                assignmentType={course.typeName}
              />
            }
            courseType={course.typeName}
            typeName={course.typeName}
            imageSrc={course.courseImage ? course.courseImage : coursePic}
            courseDescription={course.content}
            passFail={course.passFail}
            absent={course.absent}
            number={course.number}
            manualName={course.manualName}
          />
        );
      })}
    </Box>
  ) : (
    <MyCoursesEmptyStateView />
  );
}

MyCourses.propTypes = {
  courses: PropTypes.any,
  sortOptions: PropTypes.any,
  sortOrder: PropTypes.any,
  refetchAssignments: PropTypes.func,
  expandedAssignments: PropTypes.any,
  setExpandedAssignments: PropTypes.func
};
